import PageHeading from "@/presentation/ui/layouts/page/PageHeader";
import { Button, Division, Flex, Heading, Icon, Icons, Input, Popover } from "@ir/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { PromotionsFilterPopover } from "./PromotionsFilterPopover";
import { usePromotions } from "../hooks/usePromotions";

const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_LENGTH = 100;

interface PromotionsPageHeaderProps {
  isOpenFilterPopover: boolean;
  setIsOpenFilterPopover: (isOpen: boolean) => void;
}

const PromotionsPageHeader = ({ isOpenFilterPopover, setIsOpenFilterPopover }: PromotionsPageHeaderProps) => {
  const filterRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpenSearchInfoPopover, setIsOpenSearchInfoPopover] = useState(false);
  const [searchTextTempValue, setSearchTextTempValue] = useState("");
  const { getPromotions, filterInfo, setFilter, setCurrentPage } = usePromotions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpenSearchInfoPopover(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [setIsOpenSearchInfoPopover]);

  useEffect(() => {
    setTimeout(() => {
      setSearchTextTempValue(filterInfo?.search ?? "");
    }, 0);
  }, [filterInfo?.search]);

  const handleSearch = useCallback(
    async (value: string) => {
      const searchValue = value.trim();
      setSearchTextTempValue(searchValue);
      setFilter({
        ...filterInfo,
        search: searchValue.length > 0 ? searchValue : null,
      });
      setCurrentPage(1);
      await getPromotions({ isLoading: false, isRefreshing: true });
    },
    [filterInfo, getPromotions, setFilter, setCurrentPage]
  );

  const handleClear = useCallback(async () => {
    setSearchTextTempValue("");
    setFilter({
      ...filterInfo,
      search: null,
    });
    setCurrentPage(1);
    await getPromotions({ isLoading: false, isRefreshing: true });
  }, [filterInfo, setFilter, setCurrentPage, getPromotions]);

  return (
    <PageHeading className="trade-promotions-page-heading" borderBottom={true}>
      <Flex justifyContent="space-between" align="center">
        <Heading text="Trade Promotions" level={5} className="m-0 font-bold" />
        <Division style={{ position: "relative", width: "400px" }} role="search" aria-label="Search promotions">
          <Input
            size="small"
            type="text"
            placeholder="Search"
            aria-label="Search promotions"
            id="promotions-search"
            className="rounded-medium"
            allowClear={true}
            minLength={MIN_SEARCH_LENGTH}
            maxLength={MAX_SEARCH_LENGTH}
            showCharCount={true}
            value={searchTextTempValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTextTempValue(e.target.value)}
            onClear={handleClear}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                handleSearch(e.currentTarget.value);
              }
            }}
            prefix={
              <Popover
                focusable={true}
                isOpen={isOpenSearchInfoPopover}
                placement="bottom left"
                isNonModal={true}
                shouldCloseOnInteractOutside={() => true}
                defaultOpen={true}
                content={
                  <Division className="text-xs text-grey-700" ref={popoverRef}>
                    <ul style={{ listStyleType: "circle", paddingLeft: "16px" }}>
                      <li className="mb-1">
                        Search by <b>promo name</b> or <b>purchaser</b>
                      </li>
                      <li className="mb-1">
                        Minimum search length is <b>{MIN_SEARCH_LENGTH} characters</b>
                      </li>
                      <li className="mb-1">
                        Maximum search length is <b>{MAX_SEARCH_LENGTH} characters</b>
                      </li>
                    </ul>
                  </Division>
                }
              >
                <Button variant="outline" size="small" noStyle onPress={() => setIsOpenSearchInfoPopover(!isOpenSearchInfoPopover)} aria-label="Open search info">
                  <Icon name={Icons.InfoCircle} size="small" color="grey" style={{ color: "var(--grey-400)" }} />
                </Button>
              </Popover>
            }
            suffix={
              <PromotionsFilterPopover
                isOpenFilterPopover={isOpenFilterPopover}
                setIsOpenFilterPopover={setIsOpenFilterPopover}
                filterRef={filterRef}
              />
            }
          />
        </Division>
      </Flex>
    </PageHeading>
  );
};

export { PromotionsPageHeader };
